import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AxiosError } from 'axios';
import { toast } from 'sonner';
import { ArrowPathIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import AuthLayout from './components/AuthLayout';
import { useLogin, resendVerificationEmail } from './api/auth';
import { ValidationErrors } from './types';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';

export default function Login() {
  useDocumentTitle('Sign In', 'Sign in to your account to find decision makers and manage your leads.');
  const navigate = useNavigate();
  const { mutateAsync: login, isPending } = useLogin();

  const [formData, setFormData] = useState({ email: '', password: '' });
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [unverifiedEmail, setUnverifiedEmail] = useState<string | null>(null);
  const [isResending, setIsResending] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    setUnverifiedEmail(null);
    try {
      await login(formData);
      navigate('/dashboard');
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string; error?: string; errors: ValidationErrors }>;
      if (axiosError.response?.status === 403 && axiosError.response.data.error === 'email_not_verified') {
        setUnverifiedEmail(formData.email);
      } else if (axiosError.response?.status === 422) {
        setErrors(axiosError.response.data.errors || {});
        toast.error(axiosError.response.data.message || 'Please check the form for errors.');
      }
    }
  };

  const handleResend = async () => {
    if (!unverifiedEmail) return;
    setIsResending(true);
    try {
      await resendVerificationEmail(unverifiedEmail);
      toast.success('Verification email sent. Please check your inbox.');
    } catch {
      toast.error('Failed to resend. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <AuthLayout mode="login" title="Welcome back" subtitle="Enter your details to access your account.">
      <form onSubmit={handleSubmit} className="space-y-4">

        {/* Email */}
        <div>
          <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-widest mb-2">
            Email address
          </label>
          <input
            type="email"
            placeholder="you@company.com"
            required
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            className={`w-full px-4 py-3.5 bg-white border rounded-xl text-sm text-slate-900 placeholder-slate-400 transition-all outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 ${
              errors.email ? 'border-red-400 focus:ring-red-500/20 focus:border-red-400' : 'border-slate-200 hover:border-slate-300'
            }`}
          />
          {errors.email?.[0] && (
            <p className="mt-1.5 text-xs font-medium text-red-500">{errors.email[0]}</p>
          )}
        </div>

        {/* Password */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-widest">
              Password
            </label>
            <Link
              to="/forgot-password"
              className="text-xs font-semibold text-brand-700 hover:text-brand-900 transition-colors"
            >
              Forgot password?
            </Link>
          </div>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              placeholder="••••••••"
              required
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              className={`w-full px-4 py-3.5 pr-11 bg-white border rounded-xl text-sm text-slate-900 placeholder-slate-400 transition-all outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 ${
                errors.password ? 'border-red-400' : 'border-slate-200 hover:border-slate-300'
              }`}
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute inset-y-0 right-3.5 flex items-center text-slate-400 hover:text-slate-600 transition-colors"
              tabIndex={-1}
            >
              {showPassword ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
            </button>
          </div>
          {errors.password?.[0] && (
            <p className="mt-1.5 text-xs font-medium text-red-500">{errors.password[0]}</p>
          )}
        </div>

        {/* Unverified email banner */}
        {unverifiedEmail && (
          <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
            <svg className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <div className="flex-1">
              <p className="text-sm font-bold text-amber-800">Email not verified</p>
              <p className="text-xs text-amber-700 mt-0.5 leading-relaxed">
                Please verify your email before logging in.
              </p>
              <button
                type="button"
                onClick={handleResend}
                disabled={isResending}
                className="flex items-center gap-1.5 mt-2.5 text-xs font-bold text-amber-700 hover:text-amber-900 transition-colors disabled:opacity-50"
              >
                <ArrowPathIcon className={`w-3.5 h-3.5 ${isResending ? 'animate-spin' : ''}`} />
                {isResending ? 'Sending...' : 'Resend verification email'}
              </button>
            </div>
          </div>
        )}

        {/* Submit */}
        <button
          type="submit"
          disabled={isPending}
          className="w-full bg-brand-500 hover:bg-brand-600 disabled:opacity-60 text-slate-900 font-bold py-3.5 rounded-xl text-sm transition-all shadow-sm hover:shadow-md active:scale-[0.99]"
        >
          {isPending ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Signing in…
            </span>
          ) : 'Sign in to your account'}
        </button>
      </form>

      {/* Divider */}
      {/* <div className="relative my-6">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-slate-200" />
        </div>
        <div className="relative flex justify-center">
          <span className="px-3 bg-white text-xs text-slate-400 font-medium">Or continue with</span>
        </div>
      </div> */}

      {/* Google */}
      {/* <button
        type="button"
        className="w-full flex items-center justify-center gap-2.5 bg-white border border-slate-200 hover:border-slate-300 hover:bg-slate-50 text-slate-700 font-semibold py-3.5 rounded-xl text-sm transition-all shadow-sm"
      >
        <img src="https://www.svgrepo.com/show/475656/google-color.svg" alt="Google" className="w-4 h-4" />
        Google
      </button> */}

    </AuthLayout>
  );
}
